import React from 'react';
import { LandingPageConfig } from 'src/generated/landing-page-config-schemas/v1/type';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import router from 'next/router';
import Image from 'next/image';
import HorizontalScrollContainer from '../../components/HorizontalScrollContainer';
import useAnalytics from 'src/analytics/useAnalytics';

type Props = Extract<
  LandingPageConfig['data']['slices'][number],
  { type: 'highlighted-meals' }
> & {
  backgroundColor: string;
};
type Meal = Props['meals'][number];

const HighlightedMeals: React.FC<Props> = (props) => {
  const { title, meals, ctaButton, backgroundColor } = props;
  const { track } = useAnalytics();

  /*
    Preserve all query params from the landing page when redirecting to the menu page.
  */
  const handleMenuRedirect = () => {
    track('Landing Page CTA Clicked', { cta_location: 'menu-preview' });
    router.push({ pathname: '/menu', query: router.query });
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* Title */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '32px', md: '48px' },
            textAlign: { xs: 'left', md: 'center' },
            px: { xs: 4, md: 0 },
            width: '100%',
          }}
        >
          {title}
        </Typography>

        {/* Meals Grid */}
        <HorizontalScrollContainer itemCount={meals.length}>
          {meals?.map((meal: Meal, index: number) => (
            <Card
              key={index}
              elevation={0}
              sx={{
                width: { xs: '70vw', md: '380px' },
                flexShrink: 0,
                scrollSnapAlign: { xs: 'start', md: 'none' },
                borderRadius: 2,
                border: '1px solid #E5E5E5',
              }}
            >
              <Box sx={{ position: 'relative', height: '200px' }}>
                <Image
                  src={meal.image.url}
                  alt={meal.title}
                  fill
                  sizes="(max-width: 900px) 70vw, 380px"
                  style={{ objectFit: 'cover' }}
                />
              </Box>
              <CardContent sx={{ textAlign: 'left', p: 4 }}>
                <Box>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '28px',
                      fontWeight: 600,
                    }}
                  >
                    {meal.titleOverride || meal.title}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '20px',
                      color: '#737373',
                    }}
                  >
                    {meal.subtitleOverride || meal.subtitle}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </HorizontalScrollContainer>

        {/* CTA Button and Disclaimer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            px: { xs: 4, md: 0 },
          }}
        >
          {ctaButton && (
            <Button
              variant={ctaButton.variant}
              onClick={handleMenuRedirect}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                padding: '14px 40px',
                borderRadius: '50px',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {ctaButton.label}
            </Button>
          )}

          <Typography
            sx={{
              fontStyle: 'italic',
              fontSize: '14px',
              color: '#737373',
              textAlign: { xs: 'left', md: 'center' },
            }}
          >
            Meals shown are examples from our rotating menu and may not be
            available in all zip codes. Visit the full menu to see what's
            currently available near you.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default HighlightedMeals;
